import React, { useState } from 'react'
import styled from 'styled-components'
import Header from '../common/header'
import Footer from '../common/footer'
import { FaCheck, FaTimes, FaQuestionCircle } from 'react-icons/fa'
import { launchCheckout } from '../common/plus_components'
import { getLocale, formatPrice, yearlySavingPercent } from '../lib/locale'
import { useTranslation, useLang, localizePath } from '../lib/i18n'
import { DEFAULT_LANG } from '../lib/locale'
import { PRICING_SHORT } from '../lib/facts'
import PlusPill from '../common/plus_pill'

import logoImg from '../img/logo.webp'
import AppShowcase from '../common/app_showcase'
import StoreLinks from '../common/store_links'

const Wrapper = styled.div`
    background: #f8fafc;
    color: #0f172a;
    min-height: 100vh;
    position: relative;
    overflow: hidden;
`

const Hero = styled.div`
    text-align: center;
    padding: 60px 0 36px;
    position: relative;
    z-index: 1;
`

const HeroLogoWrap = styled.div`
    position: relative;
    display: inline-block;
`

const HeroLogo = styled.img`
    width: 64px;
    height: 64px;
    display: block;
`

const HeroLogoBadge = styled(PlusPill)`
    position: absolute;
    bottom: -4px;
    right: -10px;
    font-size: 10px;
    padding: 3px 8px;
    border-radius: 6px;
    line-height: 1;
    margin: 0;
`

const Title = styled.h1`
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 1rem;
    letter-spacing: -1.5px;
    color: #0f172a;

    @media (max-width: 600px) {
        font-size: 2.2rem;
    }
`

const Subtitle = styled.p`
    font-size: 1.1rem;
    color: #475569;
    max-width: 720px;
    margin: 0 auto 1rem;
    line-height: 1.6;
`

const TogglePrice = styled.span`
    font-weight: 500;
    margin-left: 6px;
`

const ToggleContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 15px;
    margin-bottom: 28px;
    position: relative;
    z-index: 2;
`

const ToggleLabel = styled.span`
    font-size: 0.9rem;
    font-weight: 600;
    color: ${props => props.$active ? '#0f172a' : '#94a3b8'};
    transition: color 0.2s;
`

const Switch = styled.div`
    width: 50px;
    height: 26px;
    background: #e2e8f0;
    border-radius: 20px;
    padding: 3px;
    cursor: pointer;
    position: relative;
`

const Knob = styled.div`
    width: 20px;
    height: 20px;
    background: #209fff;
    border-radius: 50%;
    transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    transform: translateX(${props => props.$isYearly ? '24px' : '0'});
`

const DiscountBadge = styled.span`
    background: rgba(5, 150, 105, 0.1);
    color: #059669;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 700;
    margin-left: 8px;
    border: 1px solid rgba(5, 150, 105, 0.2);
`

const DownloadRow = styled.div`
    display: flex;
    justify-content: center;
    margin: 56px 0 64px;
`

const Container = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 1.5rem;
    position: relative;
    z-index: 1;
`

const PricingGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1.15fr 1fr;
    gap: 1.5rem;
    align-items: start;
    margin: 0 auto 60px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
        max-width: 420px;
    }
`

const QuantityStepper = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 1rem 0 0.25rem;
`

const StepperLabel = styled.span`
    font-size: 0.85rem;
    font-weight: 600;
    color: #94a3b8;
    margin-right: 4px;
`

const StepperButton = styled.button`
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(255, 255, 255, 0.08);
    color: #f1f5f9;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    transition: background 0.15s;

    &:hover {
        background: rgba(255, 255, 255, 0.15);
    }

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }
`

const StepperInput = styled.input`
    && {
        font-size: 1.1rem;
        font-weight: 700;
        font-family: inherit;
        width: 44px;
        height: 28px;
        text-align: center;
        color: #f8fafc;
        background: none;
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 6px;
        padding: 0;
        margin: 0;
        outline: none;
        line-height: 28px;
        box-shadow: none;
        -moz-appearance: textfield;
        -webkit-appearance: none;
        appearance: none;
    }

    &&:focus {
        border-color: rgba(32, 159, 255, 0.5);
        color: #f8fafc;
        background: none;
        box-shadow: none;
    }

    &&:active {
        color: #f8fafc;
        background: none;
    }

    &&::selection {
        background: rgba(32, 159, 255, 0.3);
        color: #f8fafc;
    }

    &&:-webkit-autofill {
        -webkit-box-shadow: 0 0 0 100px transparent inset;
        -webkit-text-fill-color: #f8fafc;
    }
`

const PlanCard = styled.div`
    background: ${props => {
        if (props.$team) return 'radial-gradient(circle at 100% 0%, rgba(32, 159, 255, 0.12) 0%, transparent 60%), linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
        if (props.$highlighted) return 'radial-gradient(circle at 0% 0%, rgba(32, 159, 255, 0.15) 0%, transparent 50%), #ffffff'
        return '#ffffff'
    }};
    border-radius: 20px;
    padding: ${props => props.$highlighted ? '32px' : '28px'};
    border: 1px solid ${props => {
        if (props.$highlighted) return '#209fff'
        if (props.$team) return '#334155'
        return '#e2e8f0'
    }};
    display: flex;
    flex-direction: column;
    box-shadow: ${props => {
        if (props.$highlighted) return '0 20px 40px rgba(32, 159, 255, 0.08)'
        if (props.$team) return '0 20px 40px rgba(0, 0, 0, 0.15)'
        return '0 10px 30px rgba(0, 0, 0, 0.03)'
    }};
    position: relative;
    z-index: 2;
    overflow: hidden;
    color: ${props => props.$team ? '#f1f5f9' : 'inherit'};
`

const PlanHeader = styled.div`
    margin-bottom: ${props => props.$highlighted ? '1.25rem' : '1rem'};
`

const PlanName = styled.h3`
    font-size: ${props => props.$highlighted ? '1.3rem' : '0.85rem'};
    font-weight: 800;
    color: ${props => props.$team ? '#f8fafc' : '#0f172a'};
    margin-bottom: 0.6rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-transform: ${props => props.$highlighted ? 'none' : 'uppercase'};
    letter-spacing: 0.5px;
`

const PlanPriceWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 0.75rem;
`

const PlanPrice = styled.div`
    font-size: ${props => props.$highlighted ? '2.75rem' : '2rem'};
    font-weight: 800;
    color: ${props => props.$team ? '#f8fafc' : '#0f172a'};
    white-space: nowrap;

    span {
        font-size: 0.95rem;
        font-weight: 500;
        color: ${props => props.$team ? '#64748b' : '#94a3b8'};
        margin-left: 4px;
        text-transform: lowercase;
    }
`

const PlanPriceNote = styled.p`
    font-size: 0.8rem;
    color: ${props => props.$team ? '#64748b' : '#94a3b8'};
    margin: 0;
    white-space: nowrap;
`

const PlanDescription = styled.p`
    color: ${props => props.$team ? '#94a3b8' : '#64748b'};
    font-size: ${props => props.$highlighted ? '0.9rem' : '0.8rem'};
    line-height: 1.5;
    margin: 0;
`

const Button = styled.button`
    width: 100%;
    padding: ${props => props.$primary ? '13px' : '10px'};
    border-radius: 12px;
    font-size: ${props => props.$primary ? '0.95rem' : '0.85rem'};
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
    margin-bottom: 1.25rem;
    margin-top: 1rem;

    background: ${props => {
        if (props.$team) return 'rgba(255, 255, 255, 0.1)'
        if (props.$primary) return '#209fff'
        return 'transparent'
    }};
    color: ${props => {
        if (props.$team) return '#ffffff'
        if (props.$primary) return '#ffffff'
        return '#0f172a'
    }};
    border: ${props => {
        if (props.$team) return '1px solid rgba(255, 255, 255, 0.2)'
        if (props.$primary) return 'none'
        return '1px solid #e2e8f0'
    }};
    backdrop-filter: ${props => props.$team ? 'blur(8px)' : 'none'};

    &:hover {
        background: ${props => {
            if (props.$team) return 'rgba(255, 255, 255, 0.18)'
            if (props.$primary) return '#1d8ee6'
            return '#f1f5f9'
        }};
    }
`

const FeatureList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`

const CircleCheckWrapper = styled.div`
    width: 20px;
    height: 20px;
    background: ${props => props.$vibrant ? '#209fff' : '#e2e8f0'};
    color: ${props => props.$vibrant ? '#ffffff' : '#64748b'};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 10px;
`

const CircleCheck = ({ vibrant }) => (
    <CircleCheckWrapper $vibrant={vibrant}>
        <FaCheck />
    </CircleCheckWrapper>
)

const HandDrawnCheck = ({ vibrant }) => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
        <path
            d="M3.5 10.5C5 12.5 7 15 8.5 16C10 13 14 7.5 17 4.5"
            stroke={vibrant ? '#209fff' : '#8b7e6a'}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const FeatureItem = styled.li`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: ${props => props.$highlighted ? '10px' : '8px'};
    font-size: ${props => props.$highlighted ? '0.9rem' : '0.82rem'};
    color: ${props => {
        if (props.$team) return '#cbd5e1'
        if (props.$included) return '#334155'
        return '#94a3b8'
    }};
`

const HighlightBadge = styled.span`
    background: rgba(32, 159, 255, 0.1);
    color: #209fff;
    font-size: 0.7rem;
    font-weight: 800;
    text-transform: uppercase;
    padding: 4px 10px;
    border-radius: 20px;
    letter-spacing: 0.5px;
`

const DarkBubble = styled.span`
    background: #0f172a;
    color: #ffffff;
    font-size: 0.7rem;
    font-weight: 800;
    text-transform: uppercase;
    padding: 4px 10px;
    border-radius: 20px;
    letter-spacing: 0.5px;
`

const TeamHighlightBadge = styled.span`
    background: rgba(32, 159, 255, 0.15);
    color: #60b5ff;
    font-size: 0.7rem;
    font-weight: 800;
    text-transform: uppercase;
    padding: 4px 10px;
    border-radius: 20px;
    letter-spacing: 0.5px;
`

const TeamDivider = styled.div`
    height: 1px;
    background: rgba(255, 255, 255, 0.08);
    margin: 0.5rem 0 1rem;
`

const TeamPerSeat = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 0.75rem;
    padding: 10px 14px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.06);
`

const TeamPerSeatLabel = styled.span`
    font-size: 0.8rem;
    color: #94a3b8;
`

const TeamPerSeatPrice = styled.span`
    font-size: 0.85rem;
    font-weight: 700;
    color: #e2e8f0;
`

const SectionHeading = styled.h2`
    text-align: center;
    font-size: 2.5rem;
    font-weight: 800;
    color: #0f172a;
`


const ComparisonSection = styled.div`
    background: linear-gradient(180deg, #f1f5f9 0%, #f8fafc 100%);
    padding: 70px 0;
    margin: 0;
    border-top: 1px solid #e2e8f0;
    position: relative;
    z-index: 1;

    ${Container} {
        max-width: 700px;
    }
`

const ComparisonTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    background: #ffffff;
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);

    th, td {
        padding: 16px 24px;
        text-align: center;
        border-bottom: 1px solid #f1f5f9;
        position: relative;
    }

    th {
        font-weight: 800;
        color: #0f172a;
        text-align: center;
        background: #f8fafc;
        font-size: 1rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    td:first-child, th:first-child {
        text-align: left;
        width: 50%;
        color: #475569;
        padding-left: 32px;
        font-weight: 500;
    }

    .starter-col { width: 25%; }
    .plus-col { width: 25%; }

    .highlight-col {
        background: rgba(32, 159, 255, 0.03);
        border-left: 1px solid rgba(32, 159, 255, 0.1);
        border-right: 1px solid rgba(32, 159, 255, 0.1);
    }

    .cross { color: #e2e8f0; opacity: 0.5; }

    .feature-name {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .cell-content {
        display: flex;
        justify-content: center;
        align-items: center;
    }
`


const FAQSection = styled.div`
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
    padding: 70px 0 80px;
`

const FAQGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    position: relative;
    z-index: 1;

    @media (max-width: 800px) {
        grid-template-columns: 1fr;
    }
`

const FAQItem = styled.div`
    background: #ffffff;
    border: 1px solid #e2e8f0;
    padding: 24px 28px;
    border-radius: 16px;

    h4 {
        color: #0f172a;
        margin-bottom: 10px;
        font-size: 1rem;
        font-weight: 700;
        display: flex;
        align-items: center;
        gap: 10px;
    }
    p { color: #64748b; line-height: 1.65; margin: 0; font-size: 0.9rem; }
`

const COMPARISON_AVAILABILITY = [
    { free: true, plus: true },
    { free: true, plus: true },
    { free: false, plus: true, highlight: true },
    { free: false, plus: true, highlight: true },
    { free: false, plus: true, highlight: true },
    { free: false, plus: true, highlight: true },
    { free: false, plus: true, highlight: true },
    { free: false, plus: true, highlight: true },
    { free: false, plus: true, highlight: true },
    { free: false, plus: true, highlight: true },
]

const PricingPage = () => {
    const [isYearly, setIsYearly] = useState(false)
    const [teamQuantity, setTeamQuantity] = useState(5)
    const { t } = useTranslation(undefined, { keyPrefix: 'pricing' })
    const lang = useLang()
    const pricing = getLocale(lang)
    const perPeriod = isYearly ? t('perYearShort') : t('perMonthShort')
    const comparisonNames = t('comparison.features', { returnObjects: true })
    const homeHref = localizePath('/', lang)

    return (
        <Wrapper>
            <Header />
            <Container>
                <Hero>
                    <Title>{t('title')}</Title>
                    {lang === DEFAULT_LANG && <Subtitle>
                            {PRICING_SHORT[0]}
                            <br />
                            {PRICING_SHORT[1]}
                        </Subtitle>}

                    <ToggleContainer>
                        <ToggleLabel $active={!isYearly}>
                            {t('monthly')}
                            <TogglePrice>{formatPrice(pricing, pricing.monthly)}{t('perMonthShort')}</TogglePrice>
                        </ToggleLabel>
                        <Switch onClick={() => setIsYearly(!isYearly)}>
                            <Knob $isYearly={isYearly} />
                        </Switch>
                        <ToggleLabel $active={isYearly}>
                            {t('yearly')}
                            <TogglePrice>{formatPrice(pricing, pricing.yearly)}{t('perYearShort')}</TogglePrice>
                            <DiscountBadge>{t('saveBadge', { percent: yearlySavingPercent(pricing) })}</DiscountBadge></ToggleLabel>
                    </ToggleContainer>
                </Hero>

                <PricingGrid>
                    <PlanCard>
                        <PlanHeader>
                            <PlanName>{t('free.name')}</PlanName>
                            <PlanPrice>{formatPrice({ ...pricing, approx: false }, 0, { decimals: 0 })}<span>{t('perMonthShort')}</span></PlanPrice>
                            <PlanDescription>{t('free.description')}</PlanDescription>
                        </PlanHeader>
                        <Button onClick={() => window.location.href = homeHref}>{t('free.cta')}</Button>
                        <FeatureList>
                            {t('free.features', { returnObjects: true }).map((f) => (
                                <FeatureItem key={f} $included><HandDrawnCheck /> {f}</FeatureItem>
                            ))}
                        </FeatureList>
                    </PlanCard>

                    <PlanCard $highlighted>
                        <PlanHeader $highlighted>
                            <PlanName $highlighted>
                                <HeroLogoBadge style={{ position: 'static', fontSize: '0.75rem', padding: '5px 12px', borderRadius: '8px' }}>Keybreak Plus</HeroLogoBadge>
                                <HighlightBadge style={{ visibility: isYearly ? 'hidden' : 'visible' }}>{t('plus.mostPopular')}</HighlightBadge>
                            </PlanName>
                            <PlanPriceWrapper>
                                <PlanPrice $highlighted>
                                    {formatPrice(pricing, isYearly ? pricing.yearly / 12 : pricing.monthly)}
                                    <span>{t('perMonthShort')}</span>
                                </PlanPrice>
                                <PlanPriceNote>
                                    {isYearly ? t('plus.billedPerYear', { price: formatPrice(pricing, pricing.yearly) }) : ''}
                                </PlanPriceNote>
                            </PlanPriceWrapper>
                            <PlanDescription $highlighted>{t('plus.description')}</PlanDescription>
                        </PlanHeader>
                        <Button $primary onClick={() => launchCheckout(isYearly ? 'yearly' : 'monthly')}>
                            {t('plus.cta')}
                        </Button>
                        <FeatureList>
                            {t('plus.features', { returnObjects: true }).map((f, i) => (
                                <FeatureItem key={f} $highlighted $included>
                                    <HandDrawnCheck vibrant /> {i === 0 ? <strong>{f}</strong> : f}
                                </FeatureItem>
                            ))}
                        </FeatureList>
                    </PlanCard>

                    <PlanCard $team>
                        <PlanHeader>
                            <PlanName $team>
                                {t('teams.name')}
                                <TeamHighlightBadge>{t('teams.badge')}</TeamHighlightBadge>
                            </PlanName>
                            <PlanDescription $team>{t('teams.description')}</PlanDescription>
                        </PlanHeader>
                        <QuantityStepper>
                            <StepperLabel>{t('teams.seats')}</StepperLabel>
                            <StepperButton onClick={() => setTeamQuantity(q => Math.max(2, q - 1))} disabled={teamQuantity <= 2}>−</StepperButton>
                            <StepperInput
                                type="text"
                                inputMode="numeric"
                                value={teamQuantity}
                                onChange={e => {
                                    const raw = e.target.value.replace(/\D/g, '')
                                    if (raw === '') { setTeamQuantity(''); return }
                                    const n = parseInt(raw, 10)
                                    if (!isNaN(n)) setTeamQuantity(Math.min(500, n))
                                }}
                                onBlur={() => {
                                    if (teamQuantity === '' || teamQuantity < 2) setTeamQuantity(2)
                                }}
                            />
                            <StepperButton onClick={() => setTeamQuantity(q => Math.min(500, q + 1))}>+</StepperButton>
                        </QuantityStepper>
                        <TeamPerSeat>
                            <TeamPerSeatLabel>{t('teams.perSeat', {
                                price: formatPrice(pricing, isYearly ? pricing.yearly : pricing.monthly),
                                period: perPeriod,
                            })}</TeamPerSeatLabel>
                            <TeamPerSeatPrice>
                                {formatPrice(pricing, parseFloat(isYearly ? pricing.yearly : pricing.monthly) * (parseInt(teamQuantity, 10) || 0))}
                                <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#64748b', marginLeft: '2px' }}>{perPeriod}</span>
                            </TeamPerSeatPrice>
                        </TeamPerSeat>
                        <Button $team onClick={() => launchCheckout(isYearly ? 'yearly' : 'monthly', teamQuantity)}>
                            {t('teams.cta')}
                        </Button>
                        <TeamDivider />
                        <FeatureList>
                            {t('teams.features', { returnObjects: true }).map((f) => (
                                <FeatureItem key={f} $team $included><HandDrawnCheck vibrant /> {f}</FeatureItem>
                            ))}
                        </FeatureList>
                    </PlanCard>
                </PricingGrid>

                <DownloadRow>
                    <StoreLinks />
                </DownloadRow>
            </Container>


            <ComparisonSection>
                <Container>
                    <SectionHeading style={{ marginBottom: '40px' }}>{t('comparison.heading')}</SectionHeading>
                    <div style={{ maxWidth: '500px', margin: '0 auto 60px' }}>
                        <AppShowcase />
                    </div>
                    <ComparisonTable>
                        <thead>
                            <tr>
                                <th>{t('comparison.feature')}</th>
                                <th className="starter-col">{t('comparison.free')}</th>
                                <th className="plus-col highlight-col"><PlusPill>Keybreak Plus</PlusPill></th>
                            </tr>
                        </thead>
                        <tbody>
                            {COMPARISON_AVAILABILITY.map((f, i) => (
                                <tr key={i}>
                                    <td>
                                        <div className="feature-name">
                                            {comparisonNames[i]}
                                        </div>
                                    </td>
                                    <td className="starter-col">
                                        <div className="cell-content">
                                            {f.free ? <HandDrawnCheck /> : <FaTimes className="cross" />}
                                            <span className="show-for-sr">{f.free ? t('comparison.included') : t('comparison.plusOnly')}</span>
                                        </div>
                                    </td>
                                    <td className="plus-col highlight-col">
                                        <div className="cell-content">
                                            {f.plus ? (
                                                <HandDrawnCheck vibrant />
                                            ) : (
                                                <FaTimes className="cross" />
                                            )}
                                            <span className="show-for-sr">{t('comparison.included')}</span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </ComparisonTable>
                </Container>
            </ComparisonSection>

            <FAQSection>
            <Container>
                <SectionHeading style={{ marginBottom: '40px' }}>{t('faq.heading')}</SectionHeading>
                <FAQGrid>
                    {t('faq.items', { returnObjects: true }).map((item) => (
                        <FAQItem key={item.q}>
                            <h4><FaQuestionCircle /> {item.q}</h4>
                            <p>{item.a}</p>
                        </FAQItem>
                    ))}
                </FAQGrid>
            </Container>
            </FAQSection>
            <Footer />
        </Wrapper>
    )
}

export default PricingPage
